import { useState, useCallback, useMemo, type CSSProperties } from 'react';
import { TextInput, Text, Box, Input } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { normalizeTagsString, normalizeTag, tagsStringToArray } from '@/utils/tags';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import classes from './TagsField.module.css';

export interface TagsFieldProps {
  /** Platform-suggested tags the user can select */
  suggestedTags: string[];
  /** Tags this user has used on their own products before (not shown to other users) */
  previousTags?: string[];
  /** Current value: #-separated normalized string (metadata.rows[id].data['Tags']) */
  value: string;
  onChange: (normalizedTagsString: string) => void;
  /** Optional label */
  label?: string;
  /** Show required asterisk (red *) next to label */
  required?: boolean;
  /** Validation error message (e.g. required); when set, field is shown with error styling */
  error?: string | null;
  /** Placeholder for custom tag input */
  customTagPlaceholder?: string;
  /** Max tags allowed (0 = unlimited) */
  maxTags?: number;
  /** Brand accent for own/selected tag chips */
  accentColor?: string;
}

function getTagFieldStyleVars(accentColor: string): CSSProperties {
  return {
    '--tag-accent': accentColor,
    '--tag-own-bg': colorWithAlpha(accentColor, 0.06),
    '--tag-own-border': colorWithAlpha(accentColor, 0.22),
    '--tag-own-fg': accentColor,
    '--tag-selected-bg': colorWithAlpha(accentColor, 0.12),
    '--tag-selected-border': colorWithAlpha(accentColor, 0.34),
    '--tag-selected-fg': accentColor,
  } as CSSProperties;
}

/**
 * First-class Tags field: suggested tags as selectable chips + custom tag input.
 * Value is normalized #tag1#tag2 string for metadata.
 */
export function TagsField({
  suggestedTags,
  previousTags = [],
  value,
  onChange,
  label,
  required = false,
  error,
  customTagPlaceholder,
  maxTags = 0,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: TagsFieldProps) {
  const { t } = useTranslation();
  const [customInput, setCustomInput] = useState('');

  const selected = tagsStringToArray(value);

  const previousTagKeys = useMemo(
    () => new Set(previousTags.map((tag) => tag.toLowerCase())),
    [previousTags]
  );

  const suggestionChips = useMemo(() => {
    const seen = new Set<string>();
    const chips: { tag: string; isOwn: boolean }[] = [];
    for (const tag of previousTags) {
      const key = tag.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        chips.push({ tag, isOwn: true });
      }
    }
    for (const tag of suggestedTags) {
      const key = tag.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        chips.push({ tag, isOwn: false });
      }
    }
    return chips;
  }, [previousTags, suggestedTags]);

  const addTag = useCallback(
    (tag: string) => {
      const normalized = normalizeTag(tag);
      if (!normalized || (maxTags > 0 && selected.length >= maxTags)) return;
      if (selected.some((t) => t.toLowerCase() === normalized.toLowerCase())) {
        notifications.show({
          title: t('editProduct.tags.tagAlreadyAdded'),
          message: undefined,
          color: 'yellow',
        });
        return;
      }
      onChange(normalizeTagsString([...selected, normalized].join('#')));
    },
    [selected, maxTags, onChange, t]
  );

  const removeTag = useCallback(
    (tag: string) => {
      onChange(normalizeTagsString(selected.filter((t) => t !== tag).join('#')));
    },
    [selected, onChange]
  );

  const handleSuggestedClick = (tag: string) => {
    if (selected.includes(tag)) removeTag(tag);
    else addTag(tag);
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const raw = customInput.trim().replace(/^#+/, '');
      if (raw) {
        addTag(raw);
        setCustomInput('');
      }
    }
  };

  const getSuggestionChipClass = (tag: string, isOwn: boolean) => {
    const isSelected = selected.includes(tag);
    return [
      classes.chip,
      isOwn && !isSelected ? classes.chipOwn : '',
      isSelected ? classes.chipSelected : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <Box
      className={classes.field}
      style={{
        ...getTagFieldStyleVars(accentColor),
        ...(error
          ? {
              border: '1px solid var(--mantine-color-red-5)',
              borderRadius: 'var(--mantine-radius-sm)',
              padding: 'var(--mantine-spacing-xs)',
            }
          : undefined),
      }}
    >
      {label && (
        <Input.Label required={required} mb="xs" style={{ color: error ? 'var(--mantine-color-red-6)' : undefined }}>
          {label}
        </Input.Label>
      )}

      <Text size="xs" c="dimmed" mb="xs">
        {previousTagKeys.size > 0
          ? t('editProduct.tags.suggestedWithYours')
          : t('editProduct.tags.suggested')}
      </Text>
      <div className={`${classes.chipRow} mb-md`} style={{ marginBottom: 'var(--mantine-spacing-md)' }}>
        {suggestionChips.map(({ tag, isOwn }) => (
          <button
            key={tag}
            type="button"
            className={getSuggestionChipClass(tag, isOwn)}
            onClick={() => handleSuggestedClick(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <>
          <Text size="xs" c="dimmed" mb="xs">
            {t('editProduct.tags.selected')}
          </Text>
          <div className={classes.chipRow} style={{ marginBottom: 'var(--mantine-spacing-md)' }}>
            {selected.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`${classes.chip} ${classes.chipSelected}`}
                onClick={() => removeTag(tag)}
              >
                {tag}
                <span className={classes.chipRemove} aria-hidden>
                  ×
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <TextInput
        placeholder={customTagPlaceholder ?? t('editProduct.tags.customPlaceholder')}
        value={customInput}
        onChange={(e) => setCustomInput(e.currentTarget.value)}
        onKeyDown={handleCustomKeyDown}
        size="sm"
      />
      {error && (
        <Text size="xs" c="red" mt="xs">
          {error}
        </Text>
      )}
    </Box>
  );
}
